import {
  getSuppliers,
  removeSupplier,
  setSupplierFilters,
  updateSupplier,
} from '@state/reducers/supplierReducer'
import { GridColumns } from '@mui/x-data-grid-pro'
import { SupplierDto } from '@services/dtos'
import SupplierForm from '@components/forms/SupplierForm'
import { useEffect, useMemo } from 'react'
import BaseConfigTable from './BaseConfigTable'
import { useAppDispatch } from '@hooks/reduxHooks'
import { AppDispatch } from '@state/store'
import { SupplierContactTypeLabels, SupplierPaymentMethodLabels, SupplierTypeLabels } from '@utils/constants'

export default function SuppliersTable() {
  const dispatch = useAppDispatch()
  useEffect(() => {
    return () => {
      dispatch(setSupplierFilters({ includeInactive: false }))
    }
  }, [])
    
  const columns: GridColumns<SupplierDto> = useMemo(
    () => [
      { field: 'name', headerName: 'Nom', width: 200 },
      { field: 'siret', headerName: 'SIRET', width: 140 },
      { field: 'type', headerName: 'Type', width: 160, valueGetter: (params) => SupplierTypeLabels[params.row.type] },
      { field: 'address', headerName: 'Adresse', width: 300 },
      { field: 'contacts', headerName: 'Contacts', width: 300, valueGetter: (params) => 
          params.row.contacts
            .map(({ type, name, email, phone }) =>
                `(${SupplierContactTypeLabels[type]}) ${[name, email, phone]
                  .filter(Boolean)
                  .join(' - ')}`
            )
            .join(', ')},
      { field: 'hasBankInfo', headerName: 'Info bancaires', width: 120, type: 'boolean', valueGetter: (params) => {
          const { countryCode, controlKey, bankCode, branchCode, accountNumber, swiftCode } = params.row
          return !!(countryCode || controlKey || bankCode || branchCode || accountNumber || swiftCode)
        }},
      { field: 'paymentMethods', headerName: 'Méthodes paiement', width: 155, valueGetter: (params) => {
          return (params.row.paymentMethods ?? [])
            .map((method) => SupplierPaymentMethodLabels[method])
            .join(', ')
        }},
      { field: 'cyclotron', headerName: 'Cyclotron', type: 'boolean', width: 90 },
      { field: 'productionSites', headerName: 'Lieux production', width: 140, valueGetter: (params) => (params.row.productionSites ?? []).join(', ') },
      { field: 'customerSites', headerName: 'N° client / site', width: 250, valueGetter: (params) => (params.row.customerSites ?? []).map(({ site, customerNumber }) => `${site.label} / ${customerNumber}`).join(', ') },
      { field: 'notes', headerName: 'Notes', width: 300 },
    ],
    [],
  )

  return (
    <BaseConfigTable<SupplierDto>
      columns={columns}
      selectRows={(state) => state.supplier.items}
      fetchThunk={() => async (dispatch: AppDispatch) => {
        dispatch(setSupplierFilters({ includeInactive: true }))
        return dispatch(getSuppliers())
      }}
      deleteThunk={removeSupplier}
      toggleThunk={(id, isActive) => updateSupplier({ id, dto: { isActive } })}
      updatePageThunk={(page) => async (dispatch: AppDispatch) => {
        dispatch(setSupplierFilters({ page }))
        dispatch(getSuppliers())
      }}
      FormComponent={({ editingItem, onClose }) => (
        <SupplierForm editingSupplier={editingItem} onClose={onClose} />
      )}
      formMaxWidth='md'
      resourceLabel="fournisseur"
      resourcePrefix="le "
    />
  )
}
